import { pathOr } from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOffersCount } from 'shared/map-search/actions/offersCount';
import { IFeaturesMap, IRBushItem } from 'shared/map-search/types/map';
import { TMapBounds } from 'shared/map-search/types/mapBounds';
import { IApplicationState } from 'shared/map-search/types/redux';
import { EResultsStatus } from 'shared/map-search/types/results';

import { useFeaturesContext } from '../context';

const DEFAULT_FEATURES_VALUE = {};

export const OffersCounter: React.FC = () => {
  const dispatch = useDispatch();
  const { bounds, precision } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const { rbush } = useFeaturesContext();
  const resultsStatus = useSelector<IApplicationState, EResultsStatus>(state => state.results.status);
  const features = useSelector<IApplicationState, IFeaturesMap>(state =>
    pathOr<IFeaturesMap>(DEFAULT_FEATURES_VALUE, [precision, 'features'], state.features),
  );

  React.useEffect(() => {
    if (!bounds || resultsStatus === EResultsStatus.Loading) {
      return;
    }

    const featuresInBbox = rbush.search({
      minX: bounds[0][0],
      minY: bounds[0][1],
      maxX: bounds[1][0],
      maxY: bounds[1][1],
    });

    /**
     * Идентификаторы ЖК, чей главный пин попал в видимую область.
     */
    const mainPinNewbuildingIds = new Set<number>();
    featuresInBbox.forEach((rbushItem: IRBushItem) => {
      const { isNewbuildingMainPin, newbuilding } = rbushItem.feature.properties;

      if (isNewbuildingMainPin && newbuilding?.id) {
        mainPinNewbuildingIds.add(newbuilding.id);
      }
    });

    const totalCount: number = featuresInBbox.reduce((acc: number, rbushItem: IRBushItem) => {
      const { isExtended, isNewbuildingMainPin, newbuilding, count } = rbushItem.feature.properties;

      // считаем количество объявлений без учета extended объявлений
      if (isExtended) {
        return acc;
      }

      // считаем количество объявлений без учета объявлений уже посчитанных в главном пине жк
      if (!isNewbuildingMainPin && newbuilding?.id && mainPinNewbuildingIds.has(newbuilding.id)) {
        return acc;
      }

      return acc + count;
    }, 0);

    dispatch(setOffersCount(totalCount));
  }, [bounds, dispatch, rbush, resultsStatus, features, precision]);

  return null;
};
